import PropTypes from 'prop-types'

import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Document, Page, pdfjs} from 'react-pdf'

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import Button from './mui_base_components/button/Button'
import LargeModal from './mui_base_components/modals/large_modal/LargeModal'

import {base64ToUint8Array} from '../lib/fileDownload'

import downloadIcon from '../shared_assets/download-icon.svg'
import fitToHeightIcon from '../shared_assets/fit-to-height-icon.svg'
import fitToWidthIcon from '../shared_assets/fit-to-width-icon.svg'
import printIcon from '../shared_assets/print-icon.svg'
import zoomInIcon from '../shared_assets/zoom-in-icon.svg'
import zoomOutIcon from '../shared_assets/zoom-out-icon.svg'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

import './pdfViewer.scss'

pdfjs.GlobalWorkerOptions.workerSrc = '/pdfjs-dist/build/pdf.worker.min.mjs'

const PdfViewer = ({
  error = null,
  file = null,
  isLoading,
  isModalOpen,
  onCloseModal,
  onDownloadClick,
  pdfName = 'Document.pdf'
}) => {
  const CONTAINER_PADDING = 32
  const MAX_SCALE = 3
  const MIN_SCALE = 0.5
  const RESIZE_DEBOUNCE_MS = 100
  const ZOOM_INCREMENT = 0.2

  const containerRef = useRef(null)
  const pageRefs = useRef([])

  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false)
  const [hasCalculatedScale, setHasCalculatedScale] = useState(false)
  const [numberOfPages, setNumberOfPages] = useState(null)
  const [pageDimensions, setPageDimensions] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [scale, setScale] = useState(1)
  const [scaleMode, setScaleMode] = useState('fitToWidth')

  const memoizedDocumentFile = useMemo(() => {
    if (!file) return null

    const bytes = base64ToUint8Array(file)
    const bytesCopy = bytes.slice()

    return {data: bytesCopy}
  }, [file])

  const errorMessageElement = useCallback(() => (
    <div className='pdf-viewer__error-message-element'>
      <div className='pdf-viewer__error-message'>
        Preview not available. Please download to view.
      </div>
      <Button className='pdf-viewer__download-button' onClick={onDownloadClick}>
        Download
      </Button>
    </div>
  ), [onDownloadClick])

  const setScaleToFullHeight = (height, width) => {
    if (!containerRef.current) return

    const containerHeight = containerRef.current.offsetHeight
    const containerWidth = containerRef.current.offsetWidth - CONTAINER_PADDING

    const scaleX = containerWidth / width
    const scaleY = containerHeight / height
    const optimalScale = Math.min(scaleX, scaleY)

    setScale(optimalScale)
  }

  const setScaleToFullWidth = width => {
    if (!containerRef.current) return

    const containerWidth = containerRef.current.offsetWidth - CONTAINER_PADDING
    const newScale = containerWidth / width
    const clampedScale = Math.max(MIN_SCALE, Math.min(newScale, MAX_SCALE))

    setScale(clampedScale)
  }

  useEffect(() => {
    if (!isModalOpen) return

    let resizeTimeout = null

    const handleResize = () => {
      if (!pageDimensions || !containerRef.current) return

      if (resizeTimeout) clearTimeout(resizeTimeout)

      resizeTimeout = setTimeout(() => {
        const {width, height} = pageDimensions

        setScaleToFullHeight(height, width)
      }, RESIZE_DEBOUNCE_MS)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)

      if (resizeTimeout) clearTimeout(resizeTimeout)
    }
  }, [isModalOpen, pageDimensions])

  const handleClose = () => onCloseModal()

  const handleFitToHeight = () => {
    if (!containerRef.current || !pageDimensions) return

    const {width, height} = pageDimensions

    setScaleToFullHeight(height, width)
    setScaleMode('fitToHeight')
  }

  const handleFitToWidth = () => {
    if (!containerRef.current || !pageDimensions) return

    setScaleToFullWidth(pageDimensions.width)
    setScaleMode('fitToWidth')
  }

  const handleNextPage = () => {
    if (pageNumber < numberOfPages) {
      const targetPage = pageRefs.current[pageNumber]

      if (targetPage) {
        targetPage.scrollIntoView({behavior: 'smooth', block: 'start'})

        setPageNumber(pageNumber + 1)
      }
    }
  }

  const handlePageJump = event => {
    const value = Number(event.target.value)

    if (value >= 1 && value <= numberOfPages) {
      const targetPage = pageRefs.current[value - 1]

      if (targetPage) {
        targetPage.scrollIntoView({behavior: 'smooth', block: 'start'})

        setPageNumber(value)
      }
    }
  }

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      const targetPage = pageRefs.current[pageNumber - 2]

      if (targetPage) {
        targetPage.scrollIntoView({behavior: 'smooth', block: 'start'})

        setPageNumber(pageNumber - 1)
      }
    }
  }

  const handlePageLoadSuccess = page => {
    const {width, height} = page

    if (!pageDimensions)
      setPageDimensions({width, height})

    if (!hasCalculatedScale) {
      setScaleToFullWidth(width)
      setHasCalculatedScale(true)
    }
  }

  const handlePrint = () => {
    if (!file) return

    const byteArray = base64ToUint8Array(file)
    const blob = new Blob([byteArray], {type: 'application/pdf'})
    const printUrl = URL.createObjectURL(blob)
    const printWindow = window.open(printUrl)

    let urlRevoked = false

    const revokeUrl = () => {
      if (!urlRevoked) {
        URL.revokeObjectURL(printUrl)

        urlRevoked = true
      }
    }

    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus()
        printWindow.print()
        printWindow.onafterprint = () => {
          revokeUrl()
        }
        printWindow.addEventListener('pagehide', () => {
          revokeUrl()
        })
        setTimeout(revokeUrl, 60000)
      }
    } else {
      revokeUrl()
    }
  }

  const handleScroll = () => {
    const container = containerRef.current

    if (!container) return

    const containerRect = container.getBoundingClientRect()
    let mostVisiblePageIndex = 0
    let highestVisibilityRatio = -1

    for (let index = 0; index < pageRefs.current.length; index++) {
      const pageElement = pageRefs.current[index]

      if (pageElement) {
        const rect = pageElement.getBoundingClientRect()
        const visibleTop = Math.max(rect.top, containerRect.top)
        const visibleBottom = Math.min(rect.bottom, containerRect.bottom)
        const visibleHeight = Math.max(0, visibleBottom - visibleTop)

        const pageHeight = Math.max(1, rect.height)
        const ratio = visibleHeight / pageHeight

        if (ratio > highestVisibilityRatio) {
          highestVisibilityRatio = ratio
          mostVisiblePageIndex = index
        }
      }
    }

    const visiblePageNumber = mostVisiblePageIndex + 1

    if (visiblePageNumber !== pageNumber) setPageNumber(visiblePageNumber)
  }

  const handleZoomIn = () => setScale(prev =>
    Math.min(prev + ZOOM_INCREMENT, MAX_SCALE))

  const handleZoomOut = () => setScale(prev =>
    Math.max(prev - ZOOM_INCREMENT, MIN_SCALE))

  return (
    <LargeModal
      className='pdf-viewer'
      disablePortal={false}
      onClose={handleClose}
      open={isModalOpen}>
      <div className='content'>
        <div className='title'>{pdfName}</div>
        <div
          aria-label='PDF document viewer'
          className='pdf-container'
          onScroll={handleScroll}
          ref={containerRef}
          role='region'
          tabIndex={0}>
          {
            isLoading &&
            <div className='react-pdf__message'>Loading PDF...</div>
          }
          {
            !isLoading && error &&
            <div className='react-pdf__message error'>{error}</div>
          }

          {!isLoading && !error && !file && (
            <div className='react-pdf__message error'>
              Failed to load PDF
            </div>
          )}
          {!isLoading && memoizedDocumentFile && (
            <Document
              key={`${isModalOpen}-${file || ''}`}
              className='pdf-document'
              error={errorMessageElement}
              file={memoizedDocumentFile}
              onLoadSuccess={({numPages}) => {
                setIsDocumentLoaded(true)
                setNumberOfPages(numPages)
              }}>
              {
                numberOfPages &&
                Array.from(new Array(numberOfPages), (el, index) => (
                  <div
                    key={`page_container_${index + 1}`}
                    ref={element => { pageRefs.current[index] = element }}
                    className='pdf-page-container'>
                    <Page
                      className='page'
                      key={`page_${index + 1}`}
                      onLoadSuccess={handlePageLoadSuccess}
                      pageNumber={index + 1}
                      renderTextLayer={true}
                      renderAnnotationLayer={false}
                      scale={scale}
                    />
                  </div>
                ))
              }
            </Document>
          )}
        </div>
        {
          isDocumentLoaded && pageDimensions && (
            <div className='pdf-actions' data-testid='pdf-actions'>
              {
                numberOfPages > 1 &&
                <div className='page-jump'>
                  <button
                    aria-label='Previous page'
                    className='page-nav-button'
                    disabled={pageNumber === 1}
                    onClick={handlePreviousPage}
                    type='button'>
                    <ChevronLeftIcon fontSize='inherit' />
                  </button>
                  <input
                    aria-label='Page number'
                    className='page-jump-input'
                    max={numberOfPages}
                    min={1}
                    name='page-number'
                    onChange={handlePageJump}
                    type='number'
                    value={pageNumber}
                  />
                  <span className='page-separator'>/</span>
                  <span data-testid='page-total' className='page-total'>
                    {numberOfPages}
                  </span>
                  <button
                    aria-label='Next page'
                    className='page-nav-button'
                    disabled={pageNumber === numberOfPages}
                    onClick={handleNextPage}
                    type='button'>
                    <ChevronRightIcon fontSize='inherit' />
                  </button>
                </div>
              }
              <div className='zoom-controls'>
                <button
                  aria-label='Zoom out'
                  className='icon-button'
                  onClick={handleZoomOut}
                  type='button'>
                  <img
                    alt=''
                    aria-hidden='true'
                    className='icon'
                    src={zoomOutIcon}
                  />
                </button>
                <span className='zoom-level'>{Math.round(scale * 100)}%</span>
                <button
                  aria-label='Zoom in'
                  className='icon-button'
                  onClick={handleZoomIn}
                  type='button'>
                  <img
                    alt=''
                    aria-hidden='true'
                    className='icon'
                    src={zoomInIcon}
                  />
                </button>
              </div>
              {scaleMode !== 'fitToWidth' && (
                <button
                  aria-label='Fit to width'
                  className='icon-button'
                  onClick={handleFitToWidth}
                  type='button'>
                  <img
                    alt=''
                    aria-hidden='true'
                    className='icon'
                    src={fitToWidthIcon}
                  />
                </button>
              )}
              {scaleMode !== 'fitToHeight' && (
                <button
                  aria-label='Fit to height'
                  className='icon-button'
                  onClick={handleFitToHeight}
                  type='button'>
                  <img
                    alt=''
                    aria-hidden='true'
                    className='icon'
                    src={fitToHeightIcon}
                  />
                </button>
              )}
              <button
                aria-label='Download PDF'
                className='icon-button download-button'
                onClick={onDownloadClick}
                type='button'>
                <img
                  alt=''
                  aria-hidden='true'
                  className='icon'
                  src={downloadIcon}
                />
              </button>
              <button
                aria-label='Print PDF'
                className='icon-button'
                onClick={handlePrint}
                type='button'>
                <img
                  alt=''
                  aria-hidden='true'
                  className='icon'
                  src={printIcon}
                />
              </button>
            </div>
          )
        }
      </div>
    </LargeModal>
  )
}

PdfViewer.propTypes = {
  error: PropTypes.string,
  file: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onDownloadClick: PropTypes.func.isRequired,
  pdfName: PropTypes.string
}

export default PdfViewer
